import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  DotCode,
  RelatedCode
} from './models';
import { Config } from './models/config';

@Injectable({
  providedIn: 'root'
})
export class AngularDotcodesService {
  private codesCache = new Map<string, BehaviorSubject<DotCode[]>>();
  private codeCache = new Map<string, BehaviorSubject<DotCode>>();

  constructor(private http: HttpClient, @Inject('config') private config: Config) {
    if (!config) {
      throw Error('Call AngularDotcodesService with for root passing in the config object');
    }
  }

  /**
  * Method GetCode
  * @param column Column name to get Dotcode from
  * @param codeValue The code value to retrieve
  * @return Full HTTP response as Observable
  */
  public GetCode(column: string, codeValue: string): Observable<DotCode> {
    const key = `${column}-${codeValue}`;

    if (this.codeCache.get(key)) {
      this.codeCache.get(`${column}-${codeValue}`);
    } else if (this.codesCache.get(column)) {
      const codes = this.codesCache.get(column).value;
      if (codes) {
        const code = codes.filter(c => c.value === codeValue);

        if (code && code.length === 1) {
          if (!this.codeCache.get(key)) {
            this.codeCache.set(key, new BehaviorSubject<DotCode>(null));
          }

          this.codeCache.get(key).next(code[0]);
        } else {
          this.loadCodes(key, column, codeValue);
        }
      } else {
        this.loadCodes(key, column, codeValue);
      }
    } else {
      this.loadCodes(key, column, codeValue);
    }

    return this.codeCache.get(key);
  }

  private loadCodes(key: string, column: string, codeValue: string) {
    this.codeCache.set(key, new BehaviorSubject<DotCode>(null));

    const uri = `/GetCode`;
    const headers = new HttpHeaders();
    let params = new HttpParams();

    if (column !== undefined && column !== null) {
      params = params.set('column', column + '');
    }
    if (codeValue !== undefined && codeValue !== null) {
      params = params.set('codeValue', codeValue + '');
    }

    this.sendRequest<DotCode>('get', uri, headers, params, null).toPromise().then(b => {
      if (b && b.body) {
        this.codeCache.get(key).next(b.body);
      }
    });
  }
  /**
  * Method GetCodes
  * @param column Column name to get Dotcode from
  * @return Full HTTP response as Observable
  */
  public GetCodes(column: string): Observable<DotCode[]> {
    if (this.codesCache.get(column)) {
      this.codesCache.get(column);
    } else {
      this.codesCache.set(column, new BehaviorSubject<DotCode[]>(null));

      const uri = `/GetCodes`;
      const headers = new HttpHeaders();
      let params = new HttpParams();
      if (column !== undefined && column !== null) {
        params = params.set('column', column + '');
      }
      this.sendRequest<DotCode[]>('get', uri, headers, params, null).toPromise().then(b => {
        if (b && b.body) {
          this.codesCache.get(column).next(b.body);
        }
      });
    }

    return this.codesCache.get(column);
  }

  /**
  * Method GetRelatedCodes
  * @param column Column name for dotcode
  * @param codeValue DotCode value to get related codes for
  * @param relatedCode Related Column name to get Related Codes from
  * @return Full HTTP response as Observable
  */
  public GetRelatedCodes(column: string, codeValue: string, relatedCode: string): Observable<RelatedCode[]> {
    const uri = `/GetRelatedCodes`;
    const headers = new HttpHeaders();
    let params = new HttpParams();
    if (column !== undefined && column !== null) {
      params = params.set('column', column + '');
    }
    if (codeValue !== undefined && codeValue !== null) {
      params = params.set('codeValue', codeValue + '');
    }
    if (relatedCode !== undefined && relatedCode !== null) {
      params = params.set('relatedCode', relatedCode + '');
    }

    return this.sendRequest<RelatedCode[]>('get', uri, headers, params, null).pipe(
      map(b => {
        if (b && b.body) {
          return b.body;
        }
      }));
  }

  private sendRequest<T>(method: string, uri: string, headers: HttpHeaders, params: HttpParams, body: any): Observable<HttpResponse<T>> {
    headers = headers.set('Ocp-Apim-Subscription-Key', this.config.apiKey);
    if (method === 'get') {
      return this.http.get<T>(this.config.host + uri,
        { headers: headers.set('Accept', 'application/json'), params: params, observe: 'response' });
    } else if (method === 'put') {
      return this.http.put<T>(this.config.host + uri, body,
        { headers: headers.set('Content-Type', 'application/json'), params: params, observe: 'response' });
    } else if (method === 'post') {
      return this.http.post<T>(this.config.host + uri, body,
        { headers: headers.set('Content-Type', 'application/json'), params: params, observe: 'response' });
    } else if (method === 'delete') {
      return this.http.delete<T>(this.config.host + uri, { headers: headers, params: params, observe: 'response' });
    } else {
      console.error('Unsupported request: ' + method);
      return Observable.throw('Unsupported request: ' + method);
    }
  }
}
